import { useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import EditorWorkspace from '@/pages/demo-admin/components/story-studio/EditorWorkspace';
import ArticlePreview from '@/pages/demo-admin/components/story-studio/ArticlePreview';
import type { StudioForm } from '@/pages/demo-admin/components/story-studio/studioData';
import { REGIONS, STATE_GROUPS } from '@/pages/demo/revenue/components/revenueData';
import type { Region } from '@/pages/demo/revenue/components/revenueData';
import { regionName } from '@/pages/demo-admin/components/adminData';
import type { EditorialItem, EditorialStatus } from '@/pages/demo-admin/components/adminData';
import StatusBadge from '@/pages/demo-admin/components/StatusBadge';
import type { BadgeTone } from '@/pages/demo-admin/components/StatusBadge';

type Props = {
  item: EditorialItem;
  onUpdate: (patch: Partial<EditorialItem>) => void;
  onBack: () => void;
};

function toneForStatus(status: EditorialStatus): BadgeTone {
  if (status === 'Published') return 'live';
  if (status === 'Approved') return 'done';
  if (status === 'Scheduled') return 'scheduled';
  if (status === 'Revisions requested') return 'alert';
  if (status === 'Needs review') return 'review';
  if (status === 'AI-prepared') return 'review';
  return 'draft';
}

const AI_PIPELINE = ['Source captured', 'Content extracted', 'Draft prepared', 'Editorial review', 'Published'];

export default function EditorialDetail({ item, onUpdate, onBack }: Props) {
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');
  const [flash, setFlash] = useState('');

  const note = (m: string) => {
    setFlash(m);
    setTimeout(() => setFlash(''), 4200);
  };

  const onField = (key: keyof StudioForm, value: string) => onUpdate({ form: { ...item.form, [key]: value } });

  const toggleRegion = (id: string) => {
    const has = item.regionIds.includes(id);
    const next = has ? item.regionIds.filter((x) => x !== id) : [...item.regionIds, id];
    onUpdate({ regionIds: next });
  };

  const setStatus = (status: EditorialStatus) => {
    onUpdate({ status });
    if (status === 'Approved') note('Marked approved in this preview only — nothing is published.');
    else if (status === 'Revisions requested') note('Changes requested — routed back to the writer in this preview only.');
    else if (status === 'Scheduled') note('Scheduling is simulated — no story is queued in the live product.');
    else if (status === 'Published') note('Simulation only — nothing was published or saved.');
    else if (status === 'Incoming') note('Returned to the inbox in this preview only.');
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onBack} className="xl:hidden inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
          Back to inbox
        </button>
        <StatusBadge tone={toneForStatus(item.status)}>{item.status}</StatusBadge>
        <span className="font-label uppercase tracking-widest text-[10px] text-foreground-500">ID {item.id}</span>
        <span className="ml-auto"><DemoBadge label="Demo — not saved" /></span>
      </div>

      <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-tight">{item.form.headline}</h2>

      {/* Decision bar */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600 mb-3">Decision</p>
        <div className="flex flex-wrap gap-2.5">
          <button type="button" onClick={() => setStatus('Approved')} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line"></i></span>Approve
          </button>
          <button type="button" onClick={() => setStatus('Revisions requested')} className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line"></i></span>Request changes
          </button>
          <button type="button" onClick={() => setStatus('Scheduled')} className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-schedule-line"></i></span>Simulate scheduling
          </button>
          <button type="button" onClick={() => setStatus('Published')} className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-800 hover:border-foreground-800 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>Simulate publish
          </button>
          <button type="button" onClick={() => setStatus('Incoming')} className="inline-flex items-center gap-2 text-foreground-500 hover:text-secondary-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-go-back-line"></i></span>Send back to inbox
          </button>
        </div>
      </div>

      {flash && (
        <p className="bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>{flash}
        </p>
      )}

      {/* Editor + preview */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <EditorWorkspace form={item.form} onChange={onField} />
        <ArticlePreview form={item.form} mode={mode} onModeChange={setMode} />
      </div>

      {/* Story details: where it came from, AI status and where it publishes */}
      <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600 pt-2">Story details</p>
      {/* Source + AI readiness */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600">Source</p>
          <p className="mt-2 text-sm font-medium text-foreground-950">{item.source}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{item.sourceDetail}</p>
          <p className="text-xs text-foreground-500 mt-2 flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
            Received {item.received}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600">AI preparation</p>
          {item.aiPrepared ? (
            <p className="mt-2 text-sm text-foreground-800">
              A sample AI draft has been prepared for review
              <span className="ml-1 font-semibold text-accent-800">({item.aiConfidence}% sample confidence)</span>.
            </p>
          ) : (
            <p className="mt-2 text-sm text-foreground-800">Not yet prepared. In the full product this step would run automatically once the source is captured.</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {AI_PIPELINE.map((s, i) => {
              const reached = item.aiPrepared ? i <= 2 : i <= 0;
              return (
                <span key={s} className={`font-label uppercase tracking-wide text-[9px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${reached ? 'bg-primary-100 border-primary-200 text-primary-700' : 'bg-background-100 border-background-200 text-foreground-500'}`}>{s}</span>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-foreground-500">Concept only — no AI model is connected in this preview.</p>
        </div>
      </div>

      {/* Regions */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600">Publication regions</p>
          <p className="text-xs text-foreground-500">{item.regionIds.length} selected · {item.regionIds.map(regionName).join(', ') || 'None'}</p>
        </div>
        <div className="mt-3 space-y-3">
          {STATE_GROUPS.map((group) => (
            <div key={group.state}>
              <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500 mb-1.5">{group.state}</p>
              <div className="flex flex-wrap gap-2">
                {group.regions.map((r: Region) => {
                  const on = item.regionIds.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRegion(r.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                      <span className="w-3.5 h-3.5 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                      {r.city}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-foreground-500">Stories can target one or many regions; multi-region stories are counted once under their primary region in reports.</p>
      </div>

      <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line"></i></span>
        Edits, approvals and publishing here are visual only — no database write and no live publishing occurs. {REGIONS.length} markets available.
      </p>
    </div>
  );
}